import React, { useCallback, useMemo, useState } from 'react';
import { ButtonBlock, Container, Content, Div, Filter, SelectText, TaskIcons, Title } from './styles';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import {
  BUTTON_TYPE,
  ICON_SIZE,
  MY_TASKS_GROUP_FILTER_TYPE,
  MY_TASKS_GROUP_FILTER_TYPE_LABELS,
  TAB_BUTTON_TYPE
} from '../../../../../global/constants';
import SVGIcon from '../../../../../assets/images/svg/SVGIcon';
import Modal from '../../../../../component/modals/modal';
import Button from '../../../../../component/button';
import { MobxMyTaskModelInterface } from '../../../../../interfaces/TaskInterface';
import { updateUserWorkspacePreference } from '../../../../../helpers/firebaseHelper';
import { Dropdown, DropdownItem } from '../../../../../component/dropdown';
import { rootStore } from '../../../../../mobx/rootStore';
import { observer } from 'mobx-react-lite';
import CreateTaskModalV2 from '../../../../../component/modals/createTaskModal';
import ResponsiveHeader from '../../../../../component/responsiveHeader';
import { useMobileDevice } from '../../../../../global/useMobile';
import { menuItems } from '../../../../../global/row';
import Tabs from '../../../../../component/tabs';
import { ROUTES } from '../../../../../routes/urls';

interface Props {
  membersTask?: MobxMyTaskModelInterface[];
  loadData: () => void;
}

const Navbar: React.FC<Props> = (props) => {
  // States
  const [openTaskModel, setOpenTaskModel] = useState(false);

  // Mobx store variables
  const { taskStore, workspaceStore } = rootStore;
  const { myTasksFilter } = taskStore;
  const { currentWorkspace } = workspaceStore;

  // Other variables
  const history = useHistory();
  const params: { id: string } = useParams();
  const location: { pathname: string } = useLocation();
  const memberDetail = currentWorkspace?.users?.find((x) => x?.id === params?.id);
  const isMobile = useMobileDevice();

  // handle overview button active or not
  const isOverview = useMemo(() => {
    return location?.pathname === `/members/${params?.id}/overview`;
  }, [params?.id, location?.pathname]);

  // handle task button active or not
  const isTask = useMemo(() => {
    return location?.pathname === `/members/${params?.id}/tasks`;
  }, [params?.id, location?.pathname]);

  // for create task
  const createTaskModel = useCallback(async () => {
    if (memberDetail?.id) {
      taskStore.createTaskInput.addAssignee(memberDetail?.id);
      setOpenTaskModel(true);
    }
  }, [memberDetail?.id, taskStore.createTaskInput]);

  // for close task model
  const closeTaskModel = useCallback(() => {
    setOpenTaskModel(false);
  }, []);

  // handle filter based on group
  const onChangeGroupByFilter = useCallback(
    async (key: number) => {
      key = Number(key);
      taskStore.setMyTasksFilter(key);
      // Update my task filter in workspace preference
      await updateUserWorkspacePreference({ my_task_filter: { group_by: key } });
    },
    [taskStore]
  );

  const renderMoreOption = useCallback(() => {
    return (
      <Dropdown
        modalTitle='Group By'
        content={
          <Filter isHide={myTasksFilter === MY_TASKS_GROUP_FILTER_TYPE.NONE}>
            <SVGIcon
              name='small-filters-icon'
              width={ICON_SIZE.MD}
              height={ICON_SIZE.MD}
              viewBox={`0 0 ${ICON_SIZE.LG} ${ICON_SIZE.LG}`}
              className='filters-icon-color'
            />
            <SelectText isHide={myTasksFilter === MY_TASKS_GROUP_FILTER_TYPE.NONE}>
              {myTasksFilter === MY_TASKS_GROUP_FILTER_TYPE.NONE
                ? ''
                : myTasksFilter !== undefined
                ? MY_TASKS_GROUP_FILTER_TYPE_LABELS[myTasksFilter]
                : ''}
            </SelectText>
          </Filter>
        }
        trigger='click'>
        {menuItems.map((item) => {
          return <DropdownItem key={item.key} label={item.label} onClick={() => onChangeGroupByFilter(item?.key)} />;
        })}
      </Dropdown>
    );
  }, [myTasksFilter, onChangeGroupByFilter]);

  // renders tab option overview and Tasks
  const renderTabButton = useCallback(() => {
    return (
      <ButtonBlock>
        <Tabs
          title='Overview'
          size='small'
          variant={TAB_BUTTON_TYPE.STANDARD}
          isActive={isOverview}
          onClick={() => history.push(ROUTES.MEMBER_OVERVIEW.replace(':id', params?.id || ''))}
        />
        <Tabs
          title='Tasks'
          size='small'
          variant={TAB_BUTTON_TYPE.STANDARD}
          isActive={isTask}
          onClick={() => history.push(ROUTES.MEMBER_TASKS.replace(':id', params?.id || ''))}
        />
      </ButtonBlock>
    );
  }, [history, isOverview, isTask, params?.id]);

  return (
    <>
      {!isMobile ? (
        <Container>
          <Div>
            <Content>
              <Title>{memberDetail?.name}</Title>
              {renderTabButton()}
            </Content>
            {props?.membersTask && props?.membersTask[0]?.tasks?.length > 0 && props?.membersTask?.length > 0 && (
              <TaskIcons>
                {renderMoreOption()}
                {/* className 'hide-button' is used to hide button in responsive screen and shows only responsive button icon  */}
                <div className='hide-button'>
                  <Button
                    title='New Task'
                    variant={BUTTON_TYPE.PRIMARY}
                    iconName='plus-icon'
                    iconPosition='start'
                    buttonType='button'
                    onClick={createTaskModel}
                    size='small'
                  />
                </div>
              </TaskIcons>
            )}
          </Div>
        </Container>
      ) : (
        <>
          <ResponsiveHeader title={memberDetail?.name || ''} leftElement={renderMoreOption()} noMargin={true} />
          {renderTabButton()}
        </>
      )}
      <Modal open={openTaskModel} maxWidth={'680px'} onClose={closeTaskModel} isCreateTask={true}>
        <CreateTaskModalV2 loadData={props?.loadData} onCancel={closeTaskModel} />
      </Modal>
    </>
  );
};
export default observer(Navbar);
